/**
 * Top Players leaderboard for the Stats dashboard's Spotlight section, sourced from
 * the Owners Hub Stats API `daily_player_activity` data set (one row per player,
 * keyed by `email`).
 *
 * The window follows the SAME range selector the rest of the page uses
 * ({@link homeWindow}); a single `/aggregations/data-set` aggregation runs per
 * (brand, range), refreshed in place on the live `TODAY` cadence.
 *
 * Ranking ("money wagered"): the data set exposes only single-field server-side
 * ordering, so we over-fetch the top {@link FETCH_LIMIT} by `casino_bet_amount`
 * (the dominant wager field, server `max_limit` is 50), then rank the rows by TOTAL
 * stakes (casino + sport) client-side and keep the top {@link TOP_N}. This matches
 * the page's "Stakes" convention (casino + sport) and keeps the shown value equal to
 * the sort key. Caveat: a player outside the top-50-by-casino who is high on
 * sport-only wagering would not surface (negligible for a casino-first brand);
 * revisit if a combined-wager order field lands.
 *
 * Identifier: the data set returns only `email` (no player id / display name), so the
 * row name is the email; the brand owner is authorized to see their own players.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSet,
  playerActivityRows,
  selectStatsDataSet,
  selectStatsDataSetError,
  selectStatsDataSetLoading,
  type StatsDataSetRef,
  type StatsOrderDir,
} from '@/store/slices/ownerHubStats';

import type { HomePeriod, StatsTopTable } from '../../mock-data/types';
import { AMOUNT_SCALE, homeWindow } from '../home/stats-mapping';
import { LIVE_REFRESH_MS, useRefreshTick } from '../home/useRefreshTick';

/** Per-player data set (paginated, ordered, keyed by `email`). */
const DATA_SET = 'daily_player_activity';
/** Micro-units → major currency unit (same factor the rest of the page uses). */
const MONEY = 1 / AMOUNT_SCALE;
/** Rows shown on the board. */
const TOP_N = 5;
/** Over-fetch (server `max_limit`) so the client-side total-stakes ranking is robust. */
const FETCH_LIMIT = 50;
/** Server-side sort hint: the dominant wager component (casino bets). */
const ORDER_BY: Record<string, StatsOrderDir> = { casino_bet_amount: 'desc' };

/** One row of the `daily_player_activity` response (only the fields we read). */
type PlayerRow = {
  email?: unknown;
  casino_bet_amount?: unknown;
  sport_bet_amount?: unknown;
};

export type TopPlayersResult = {
  /** The Top Players board, shaped for the shared {@link StatsTopTable} renderer. */
  table: StatsTopTable;
};

/** Coerce a possibly-string scaled amount to a number (the API mixes both). */
const num = (value: unknown): number => {
  const n = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : 0;
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param casinoInstanceId Active brand id (`displayBrand?.id`); `undefined` keeps the
 *        board in its loading state until a brand resolves.
 * @param interval The selected range: the SAME `HomeInterval` set the page header uses.
 */
export const useTopPlayers = (casinoInstanceId: string | undefined, interval: HomePeriod): TopPlayersResult => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Only `TODAY` is live (re-fetched on the 5-min tick so the window rolls at UTC
  // midnight and the day's running totals stay fresh); other ranges fetch once.
  const live = interval === 'TODAY';
  const tick = useRefreshTick(live, LIVE_REFRESH_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow(interval, Date.now()), [interval, tick]);

  const ref: StatsDataSetRef | undefined = casinoInstanceId
    ? { casinoInstanceId, dataSet: DATA_SET, startDate: window.startDate, endDate: window.endDate, orderBy: ORDER_BY, limit: FETCH_LIMIT }
    : undefined;

  const entry = useAppSelector((s) => selectStatsDataSet(s, ref));
  const loadingFlag = useAppSelector((s) => selectStatsDataSetLoading(s, ref));
  const error = useAppSelector((s) => selectStatsDataSetError(s, ref));

  React.useEffect(() => {
    if (!casinoInstanceId) return;
    dispatch(
      fetchStatsDataSet({
        casinoInstanceId,
        dataSet: DATA_SET,
        startDate: window.startDate,
        endDate: window.endDate,
        orderBy: ORDER_BY,
        limit: FETCH_LIMIT,
      }),
    );
  }, [dispatch, casinoInstanceId, window.startDate, window.endDate, tick]);

  const rows = React.useMemo(() => {
    // `playerActivityRows` absorbs the single-player collapse (a 1-player brand comes
    // back as a flat object, not a 1-element array) so the board still ranks that row.
    const data = playerActivityRows(entry?.data) as PlayerRow[];
    return data
      .map((p) => ({
        name: typeof p.email === 'string' && p.email ? p.email : t('Unknown player'),
        value: (num(p.casino_bet_amount) + num(p.sport_bet_amount)) * MONEY,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, TOP_N);
  }, [entry, t]);

  // Stale-while-revalidate: skeleton only when there is NO cached aggregation. A
  // missing brand reads as loading; a first load that failed stops loading (falls
  // through to the empty/error state); a background refresh keeps rows on screen.
  const loading = !casinoInstanceId || (entry === undefined && (loadingFlag || error === undefined));
  // Hard error: the request failed and nothing is cached (so not loading).
  const errored = !loading && error !== undefined && entry === undefined;

  // `primaryLabel` / `secondaryLabel` are localized at the render site (Spotlight)
  // against the stable `id`; the literals here are only a fallback.
  const table: StatsTopTable = { id: 'top-players', primaryLabel: 'Top Players', secondaryLabel: 'Stakes', rows, loading, error: errored };
  return { table };
};

export default useTopPlayers;
